import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

const TABS = [
  { key: 'levels',    label: 'Stock Levels' },
  { key: 'movements', label: 'Movements' },
];

const TYPE_LABELS = { in: 'Stock In', out: 'Stock Out', adjustment: 'Adjustment' };
const TYPE_COLORS = {
  in:         'bg-green-100 text-green-700',
  out:        'bg-red-100 text-red-700',
  adjustment: 'bg-blue-100 text-blue-700',
};

function peso(centavos) {
  if (!centavos) return '—';
  return '₱' + (centavos / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtQtyNum(n) {
  if (n == null) return '0.00';
  return Number(n).toFixed(2);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtQty(qty, unit, type) {
  const sign = type === 'adjustment' && qty < 0 ? '−' : type === 'adjustment' ? '+' : '';
  return `${sign}${Number(Math.abs(qty)).toFixed(2)} ${unit}`;
}

// ─── Stock-In modal ───────────────────────────────────────────────────────────

const EMPTY_STOCK_IN = {
  productId: '', quantity: '', unitCost: '',
  supplierName: '', referenceNo: '', note: '',
  date: new Date().toISOString().slice(0, 10),
};

function StockInModal({ ingredient, allIngredients, onClose, onSaved }) {
  const { getAccessToken } = useAuth();
  const [form, setForm] = useState(() => ({
    ...EMPTY_STOCK_IN,
    productId: ingredient?._id || '',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  const selectedProduct = allIngredients.find((i) => i._id === form.productId);
  const totalCost = form.quantity && form.unitCost
    ? (parseFloat(form.quantity) * parseFloat(form.unitCost)).toFixed(2)
    : '';

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (!form.productId)          { setErr('Select an ingredient'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { setErr('Enter a valid quantity'); return; }
    if (!form.unitCost || parseFloat(form.unitCost) < 0)  { setErr('Enter unit cost (0 if unknown)'); return; }

    setSaving(true);
    try {
      const token = getAccessToken();
      await api.post('/restaurant/pos-reports/inventory/stock-in', {
        productId:    form.productId,
        quantity:     parseFloat(form.quantity),
        unitCost:     parseFloat(form.unitCost),
        supplierName: form.supplierName || null,
        referenceNo:  form.referenceNo  || null,
        note:         form.note         || null,
        date:         form.date,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-800">Stock In</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {err && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{err}</p>}

          {/* Ingredient */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Ingredient *</label>
            {ingredient ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700">
                {ingredient.name}
                <span className="ml-2 text-gray-400 text-xs">({ingredient.stockUnit})</span>
              </div>
            ) : (
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.productId} onChange={(e) => set({ productId: e.target.value })}>
                <option value="">— select ingredient —</option>
                {allIngredients.map((i) => (
                  <option key={i._id} value={i._id}>{i.name} ({i.stockUnit})</option>
                ))}
              </select>
            )}
          </div>

          {/* Qty + unit + date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                Quantity *
                {selectedProduct?.stockUnit && (
                  <span className="ml-1 text-blue-500 font-normal normal-case">({selectedProduct.stockUnit})</span>
                )}
              </label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                type="number" min="0.001" step="any"
                value={form.quantity} onChange={(e) => set({ quantity: e.target.value })}
                placeholder="e.g. 500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Date</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                type="date"
                value={form.date} onChange={(e) => set({ date: e.target.value })} />
            </div>
          </div>

          {/* Cost */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                Unit Cost (₱) *
              </label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                type="number" min="0" step="0.01"
                value={form.unitCost} onChange={(e) => set({ unitCost: e.target.value })}
                placeholder="per unit" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Total Cost</label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                {totalCost ? `₱${parseFloat(totalCost).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : '—'}
              </div>
            </div>
          </div>

          {/* Supplier */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Supplier</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.supplierName} onChange={(e) => set({ supplierName: e.target.value })}
                placeholder="Supplier name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">DR / SI No.</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.referenceNo} onChange={(e) => set({ referenceNo: e.target.value })}
                placeholder="Reference number" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Note</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={form.note} onChange={(e) => set({ note: e.target.value })}
              placeholder="Optional note" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Receive Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Adjustment modal ─────────────────────────────────────────────────────────

function AdjustModal({ ingredient, onClose, onSaved }) {
  const { getAccessToken } = useAuth();
  const [qty, setQty] = useState('');
  const [adjType, setAdjType] = useState('subtract'); // add | subtract
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (!qty || parseFloat(qty) <= 0) { setErr('Enter a valid quantity'); return; }
    setSaving(true);
    try {
      const token = getAccessToken();
      const signedQty = adjType === 'subtract' ? -Math.abs(parseFloat(qty)) : Math.abs(parseFloat(qty));
      await api.post('/restaurant/pos-reports/inventory/adjust', {
        productId: ingredient._id,
        quantity:  signedQty,
        note:      note || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-base font-bold text-gray-800">Adjust Stock — {ingredient.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {err && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{err}</p>}

          <div className="flex gap-2">
            {['subtract', 'add'].map((t) => (
              <button key={t} type="button"
                onClick={() => setAdjType(t)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  adjType === t
                    ? t === 'subtract' ? 'bg-red-500 text-white border-red-500' : 'bg-green-600 text-white border-green-600'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}>
                {t === 'subtract' ? '− Remove / Spoilage' : '+ Add'}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              Quantity ({ingredient.stockUnit})
            </label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              type="number" min="0.001" step="any"
              value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" />
            <p className="text-xs text-gray-400 mt-1">
              Current stock: {fmtQtyNum(ingredient.stockQuantity ?? 0)} {ingredient.stockUnit}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Reason / Note</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. spoilage, correction" />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className={`flex-1 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${
                adjType === 'subtract' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'
              }`}>
              {saving ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Per-ingredient movement detail panel ─────────────────────────────────────

function IngredientDetail({ ingredient, onClose }) {
  const { getAccessToken } = useAuth();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAccessToken();
    api.get('/restaurant/pos-reports/inventory/movements', {
      headers: { Authorization: `Bearer ${token}` },
      params: { productId: ingredient._id, limit: 100 },
    })
      .then((r) => setMovements(r.data.movements || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ingredient._id, getAccessToken]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">{ingredient.name}</h2>
            <p className="text-xs text-gray-400">
              Current stock: <span className="font-semibold text-gray-700">{fmtQtyNum(ingredient.stockQuantity ?? 0)} {ingredient.stockUnit}</span>
              {ingredient.reorderPoint != null && (
                <span className="ml-2">· Reorder at: {fmtQtyNum(ingredient.reorderPoint)} {ingredient.stockUnit}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center items-center h-24">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
            </div>
          ) : movements.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No movements recorded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-xs text-gray-400 uppercase border-b">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-center px-4 py-3 font-medium">Type</th>
                  <th className="text-right px-4 py-3 font-medium">Qty</th>
                  <th className="text-right px-4 py-3 font-medium">Unit Cost</th>
                  <th className="text-right px-4 py-3 font-medium">Total</th>
                  <th className="text-left px-4 py-3 font-medium">Supplier / Ref</th>
                  <th className="text-left px-4 py-3 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m._id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-600">{fmtDate(m.date)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[m.type]}`}>
                        {TYPE_LABELS[m.type]}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-800">
                      {fmtQty(m.quantity, m.unit, m.type)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{peso(m.unitCost)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{peso(m.totalCost)}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">
                      {m.supplierName || '—'}
                      {m.referenceNo && <span className="block text-gray-400">{m.referenceNo}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{m.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RestaurantInventoryPage() {
  const { getAccessToken } = useAuth();
  const [tab, setTab] = useState('levels');

  // Stock levels state
  const [ingredients, setIngredients] = useState([]);
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [levelsError, setLevelsError] = useState(null);
  const [stockInModal, setStockInModal] = useState(null); // null | ingredient | 'new'
  const [adjustModal, setAdjustModal]   = useState(null);
  const [detailModal, setDetailModal]   = useState(null);
  const [search, setSearch] = useState('');

  // Movements state
  const [movements, setMovements] = useState([]);
  const [movTotal, setMovTotal]   = useState(0);
  const [movPage, setMovPage]     = useState(1);
  const [movLoading, setMovLoading] = useState(false);
  const [movFilters, setMovFilters] = useState({ type: '', dateFrom: '', dateTo: '' });
  const [selectedIngFilter, setSelectedIngFilter] = useState('');

  const loadLevels = useCallback(async () => {
    setLevelsLoading(true);
    setLevelsError(null);
    try {
      const token = getAccessToken();
      const res = await api.get('/restaurant/pos-reports/inventory/levels', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIngredients(res.data.ingredients || []);
    } catch (e) {
      setLevelsError(e.response?.data?.message || e.message);
    } finally {
      setLevelsLoading(false);
    }
  }, [getAccessToken]);

  const loadMovements = useCallback(async (page = 1) => {
    setMovLoading(true);
    try {
      const token = getAccessToken();
      const params = { page, limit: 50, ...movFilters };
      if (selectedIngFilter) params.productId = selectedIngFilter;
      const res = await api.get('/restaurant/pos-reports/inventory/movements', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setMovements(res.data.movements || []);
      setMovTotal(res.data.total || 0);
      setMovPage(page);
    } catch (e) {
      // silent
    } finally {
      setMovLoading(false);
    }
  }, [getAccessToken, movFilters, selectedIngFilter]);

  useEffect(() => { loadLevels(); }, [loadLevels]);
  useEffect(() => { if (tab === 'movements') loadMovements(1); }, [tab, loadMovements]);

  const filtered = ingredients.filter((i) =>
    !search || i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.category?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStockCount = ingredients.filter(
    (i) => i.reorderPoint != null && i.stockQuantity <= i.reorderPoint
  ).length;

  return (
    <Layout pageTitle="Inventory">
      <div className="p-6 bg-gray-50 min-h-screen">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ingredient Inventory</h1>
            <p className="text-sm text-gray-500">
              Track raw ingredient stock levels and deliveries
              {lowStockCount > 0 && (
                <span className="ml-2 text-red-500 font-medium">· {lowStockCount} low stock</span>
              )}
            </p>
          </div>
          <button onClick={() => setStockInModal('new')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700">
            + Receive Stock
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 mb-5 self-start w-fit">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Stock Levels tab ── */}
        {tab === 'levels' && (
          <>
            <div className="mb-4">
              <input className="w-full sm:w-64 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder="Search ingredients…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            {levelsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{levelsError}</div>
            )}

            {levelsLoading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : ingredients.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <p className="text-gray-400 text-sm">
                  No raw ingredients yet.{' '}
                  <a href="/pos/menu" className="text-blue-500 hover:underline">Add them in Menu Management.</a>
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-400 uppercase border-b">
                      <th className="text-left px-4 py-3 font-medium">Ingredient</th>
                      <th className="text-left px-4 py-3 font-medium">Category</th>
                      <th className="text-right px-4 py-3 font-medium">Received</th>
                      <th className="text-right px-4 py-3 font-medium">Used (Sales)</th>
                      <th className="text-right px-4 py-3 font-medium">Adjusted</th>
                      <th className="text-right px-4 py-3 font-medium font-semibold text-gray-600">Remaining</th>
                      <th className="text-right px-4 py-3 font-medium">Reorder At</th>
                      <th className="text-center px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((ing) => {
                      const isLow = ing.reorderPoint != null && ing.stockQuantity <= ing.reorderPoint;
                      const u = ing.stockUnit || '';
                      const totalIn  = ing.totalIn  ?? 0;
                      const totalOut = ing.totalOut ?? 0;
                      const totalAdj = ing.totalAdjustment ?? 0;

                      function qtyCell(n, colorClass) {
                        if (!n) return <span className="text-gray-300">—</span>;
                        return <span className={colorClass}>{fmtQtyNum(n)}<span className="text-gray-400 ml-0.5 text-xs">{u}</span></span>;
                      }

                      return (
                        <tr key={ing._id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800">
                            <button onClick={() => setDetailModal(ing)}
                              className="hover:text-blue-600 hover:underline text-left">
                              {ing.name}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{ing.category || '—'}</td>

                          {/* Received */}
                          <td className="px-4 py-3 text-right">{qtyCell(totalIn, 'text-green-700')}</td>

                          {/* Used */}
                          <td className="px-4 py-3 text-right text-orange-600">
                            {totalOut
                              ? <>−{fmtQtyNum(totalOut)}<span className="text-orange-400 ml-0.5 text-xs">{u}</span></>
                              : <span className="text-gray-300">—</span>}
                          </td>

                          {/* Adjusted */}
                          <td className="px-4 py-3 text-right text-blue-600">
                            {totalAdj
                              ? <>{totalAdj > 0 ? '+' : '−'}{fmtQtyNum(Math.abs(totalAdj))}<span className="text-blue-400 ml-0.5 text-xs">{u}</span></>
                              : <span className="text-gray-300">—</span>}
                          </td>

                          {/* Remaining (authoritative running balance) */}
                          <td className="px-4 py-3 text-right font-semibold">
                            <span className={isLow ? 'text-red-600' : 'text-gray-900'}>
                              {fmtQtyNum(ing.stockQuantity ?? 0)}
                            </span>
                            <span className="text-gray-400 ml-1 text-xs">{u}</span>
                          </td>

                          <td className="px-4 py-3 text-right text-gray-500">
                            {ing.reorderPoint != null
                              ? <>{fmtQtyNum(ing.reorderPoint)}<span className="text-xs text-gray-400 ml-0.5">{u}</span></>
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isLow ? (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Low</span>
                            ) : (
                              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full font-medium">OK</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => setStockInModal(ing)}
                              className="text-green-600 hover:text-green-800 text-xs mr-3 font-medium">
                              Stock In
                            </button>
                            <button onClick={() => setAdjustModal(ing)}
                              className="text-blue-500 hover:text-blue-700 text-xs mr-3">
                              Adjust
                            </button>
                            <button onClick={() => setDetailModal(ing)}
                              className="text-gray-400 hover:text-gray-600 text-xs">
                              History
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── Movements tab ── */}
        {tab === 'movements' && (
          <>
            <div className="flex flex-wrap gap-3 mb-4">
              <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={selectedIngFilter}
                onChange={(e) => { setSelectedIngFilter(e.target.value); loadMovements(1); }}>
                <option value="">All ingredients</option>
                {ingredients.map((i) => <option key={i._id} value={i._id}>{i.name}</option>)}
              </select>
              <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={movFilters.type}
                onChange={(e) => setMovFilters((f) => ({ ...f, type: e.target.value }))}>
                <option value="">All types</option>
                <option value="in">Stock In</option>
                <option value="out">Stock Out</option>
                <option value="adjustment">Adjustment</option>
              </select>
              <input type="date" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={movFilters.dateFrom}
                onChange={(e) => setMovFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
              <input type="date" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={movFilters.dateTo}
                onChange={(e) => setMovFilters((f) => ({ ...f, dateTo: e.target.value }))} />
              <button onClick={() => loadMovements(1)}
                className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700">
                Filter
              </button>
            </div>

            {movLoading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : movements.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-10 text-center">
                <p className="text-gray-400 text-sm">No movements found.</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-400 uppercase border-b">
                        <th className="text-left px-4 py-3 font-medium">Date</th>
                        <th className="text-left px-4 py-3 font-medium">Ingredient</th>
                        <th className="text-center px-4 py-3 font-medium">Type</th>
                        <th className="text-right px-4 py-3 font-medium">Qty</th>
                        <th className="text-right px-4 py-3 font-medium">Total Cost</th>
                        <th className="text-left px-4 py-3 font-medium">Supplier / Ref</th>
                        <th className="text-left px-4 py-3 font-medium">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movements.map((m) => (
                        <tr key={m._id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-gray-600">{fmtDate(m.date)}</td>
                          <td className="px-4 py-2.5">
                            <button onClick={() => setDetailModal(
                              ingredients.find((i) => i._id === (m.productId?._id || m.productId))
                            )} className="font-medium text-gray-800 hover:text-blue-600 hover:underline text-left">
                              {m.productId?.name || '—'}
                            </button>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[m.type]}`}>
                              {TYPE_LABELS[m.type]}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium text-gray-800">
                            {fmtQty(m.quantity, m.unit, m.type)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{peso(m.totalCost)}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-xs">
                            {m.supplierName || '—'}
                            {m.referenceNo && <span className="block text-gray-400">{m.referenceNo}</span>}
                          </td>
                          <td className="px-4 py-2.5 text-gray-400 text-xs">{m.note || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {movTotal > 50 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <button disabled={movPage === 1}
                      onClick={() => loadMovements(movPage - 1)}
                      className="px-3 py-1.5 border border-gray-200 rounded text-sm disabled:opacity-40 hover:bg-gray-50">
                      Prev
                    </button>
                    <span className="px-3 py-1.5 text-sm text-gray-500">
                      Page {movPage} of {Math.ceil(movTotal / 50)}
                    </span>
                    <button disabled={movPage >= Math.ceil(movTotal / 50)}
                      onClick={() => loadMovements(movPage + 1)}
                      className="px-3 py-1.5 border border-gray-200 rounded text-sm disabled:opacity-40 hover:bg-gray-50">
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {stockInModal && (
        <StockInModal
          ingredient={stockInModal === 'new' ? null : stockInModal}
          allIngredients={ingredients}
          onClose={() => setStockInModal(null)}
          onSaved={() => { setStockInModal(null); loadLevels(); if (tab === 'movements') loadMovements(1); }}
        />
      )}
      {adjustModal && (
        <AdjustModal
          ingredient={adjustModal}
          onClose={() => setAdjustModal(null)}
          onSaved={() => { setAdjustModal(null); loadLevels(); if (tab === 'movements') loadMovements(1); }}
        />
      )}
      {detailModal && (
        <IngredientDetail
          ingredient={detailModal}
          onClose={() => setDetailModal(null)}
        />
      )}
    </Layout>
  );
}
